import type { Camera } from '@babylonjs/core/Cameras/camera';
import { Effect } from '@babylonjs/core/Materials/effect';
import { PostProcess } from '@babylonjs/core/PostProcesses/postProcess';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';

import { pixelSizeOverride, renderFeatures } from './compat';

/** Screen pixels per art pixel. Larger is chunkier. */
export const PIXEL_SIZE = 3;

/**
 * Roughly how many device pixels one world unit covers at the gameplay camera.
 *
 * From the framing this scene is built around: a character is about 1.8 units tall
 * and reads as roughly 45 device pixels at `CAMERA_RADIUS` — the same measurement the
 * model and collider rules are written against. Approximate on purpose; it exists to
 * turn "is this thin thing visible" into arithmetic rather than a guess.
 */
export const PIXELS_PER_UNIT = 25;

/**
 * The thinnest a world-space outline can be and still occupy a whole output pixel.
 *
 * **The hover highlight was below this**, and that is what made a provably-correct
 * hover look broken: at 0.08 units it is about 2 device pixels, the post-process
 * renders at 1/`PIXEL_SIZE` resolution, so the rim came to two thirds of one output
 * pixel and was averaged away. Nothing errors, nothing logs, and the outline is set
 * on every part exactly as intended — it is simply too thin to survive being
 * downsampled.
 *
 * Any new world-space line — a targeting ring, a weapon trail, an elite aura — has to
 * clear this too, and `entities.test.ts` asserts the two highlights do.
 */
export const MIN_OUTLINE_WIDTH = PIXEL_SIZE / PIXELS_PER_UNIT;

/** Quantisation steps per colour channel. Lower is flatter and more graphic. */
export const COLOUR_LEVELS = 14;

/**
 * Set to false to see the raw geometry while working on the models — a pixel
 * grid hides exactly the seams you are trying to find.
 */
export const PIXELATE = true;

const SHADER_NAME = 'grindPixelArt';

/**
 * Pixelation and colour quantisation for the 3D viewport.
 *
 * Two passes in one shader:
 *
 *  1. Snap the sample point to a grid, so the world resolves into chunky texels
 *     the way a 2D ARPG would.
 *  2. Posterise each channel, then push shadows cold and lit surfaces warm, so
 *     the reduced palette lands in the same register as the HUD's tokens rather
 *     than looking merely low-colour.
 *
 * Posterising is a handful of ALU ops per pixel. A nearest-match against a
 * 16-entry palette would be sixteen distance computations per pixel — at
 * 1600×900 that difference is real, and the look is close enough that it is not
 * worth paying for. If a strictly fixed palette is wanted later, the LUT drops
 * into this same shader.
 *
 * > This is deliberately **not** implemented with `engine.setHardwareScalingLevel()`.
 * > `ScreenProjector.update()` derives its viewport from
 * > `engine.getRenderWidth()/getRenderHeight()`, so shrinking the backbuffer
 * > would divide every nameplate and damage-number anchor by the scaling factor
 * > and scatter the HUD. Rendering at full resolution and pixelating in the
 * > shader leaves that maths untouched.
 */
function registerShader(): void {
  if (Effect.ShadersStore[`${SHADER_NAME}FragmentShader`]) return;

  Effect.ShadersStore[`${SHADER_NAME}FragmentShader`] = `
    precision highp float;

    varying vec2 vUV;
    uniform sampler2D textureSampler;

    // x, y = viewport size in pixels; z = pixel size; w = colour levels.
    uniform vec4 pixelParams;

    void main(void) {
      vec2 resolution = pixelParams.xy;
      float pixelSize = max(pixelParams.z, 1.0);
      float levels = max(pixelParams.w, 2.0);

      // Snap to the centre of the nearest cell, so a cell takes one colour
      // rather than smearing across the boundary.
      //
      // The guard matters: a zero resolution — which is what the uniform holds
      // before the first resize lands — would make cells zero, snapped
      // infinite, and the whole screen a single clamped edge texel. That failure
      // looks exactly like a flat grey fill.
      vec2 uv = vUV;
      if (resolution.x >= 1.0 && resolution.y >= 1.0) {
        vec2 cells = max(resolution / pixelSize, vec2(1.0));
        uv = (floor(vUV * cells) + 0.5) / cells;
      }

      vec3 colour = texture2D(textureSampler, uv).rgb;

      // Posterise in perceptual space, not linear.
      //
      // This game is nearly all shadow. Quantising linearly spends almost every
      // step on the bright end and collapses the whole dark range onto one or
      // two values, which reads as a flat black screen. Going through a sqrt
      // curve gives the darks the steps they need and keeps the floor readable.
      vec3 perceptual = sqrt(colour);
      perceptual = floor(perceptual * levels + 0.5) / levels;
      colour = perceptual * perceptual;

      // Push the ramp: shadows a little cold, highlights a little warm. Kept
      // gentle on purpose — a strong blue lift on near-black pixels turns the
      // fogged horizon into a visible teal band.
      float luma = dot(colour, vec3(0.299, 0.587, 0.114));
      vec3 shadowTint = vec3(0.94, 0.97, 1.05);
      vec3 lightTint  = vec3(1.06, 1.00, 0.93);
      colour *= mix(shadowTint, lightTint, smoothstep(0.05, 0.5, luma));

      gl_FragColor = vec4(clamp(colour, 0.0, 1.0), 1.0);
    }
  `;
}

/**
 * Builds the effect for a camera, **detached**. Returns the post-process so the caller can attach
 * it when the player's setting asks for it (`main.tsx`), or null when pixelation is switched off.
 *
 * **Detached, since 2026-09-29.** It used to attach itself here and rely on `main.tsx` to take it
 * off again when the setting was off — which is the default. Anything that built a scene without
 * that code (every preview page a check was run through) therefore drew pixelated, and the owner
 * judged the monsters from pictures a third of their real resolution: *it's really hard to see*.
 * Off is now the state a scene starts in, everywhere.
 */
export function attachPixelArt(camera: Camera): PostProcess | null {
  if (!pixelateEnabled()) return null;

  registerShader();

  const effect = new PostProcess(
    'grind-pixel-art',
    SHADER_NAME,
    ['pixelParams'],
    null,
    1.0, // full render resolution — see the note above
    camera,
    // Nearest sampling: bilinear would blur the grid straight back out.
    Texture.NEAREST_SAMPLINGMODE,
  );
  // Built attached (Babylon's constructor does it); off until a setting says otherwise.
  camera.detachPostProcess(effect);

  const engine = camera.getScene().getEngine();

  /**
   * If the effect cannot compile, throw it away rather than keeping it in the
   * chain.
   *
   * A post-process sits between the rendered scene and the canvas: when its
   * shader fails, the player does not get an unpixelated game, they get a black
   * screen with a working HUD floating on it — the geometry renders fine into a
   * target nothing then copies out. A cosmetic effect must never be able to hide
   * the entire game, so this detaches itself and lets the raw scene through.
   */
  effect.onEffectCreatedObservable.add((shaderEffect) => {
    shaderEffect.onError = (_e, errors) => {
      console.error(
        'grind: pixel-art shader failed to compile, falling back to the raw scene\n',
        errors,
      );
      effect.dispose(camera);
    };
  });

  effect.onApply = (shaderEffect) => {
    // PostProcess.width/height are only populated once the effect has been
    // activated and sized. Falling back to the engine's own render size means
    // the very first frame is pixelated correctly rather than sampling garbage.
    const width = effect.width > 0 ? effect.width : engine.getRenderWidth();
    const height = effect.height > 0 ? effect.height : engine.getRenderHeight();

    // `?px=N` wins over the constant, so the chunkiness can be judged by looking at it on a
    // real GPU rather than rebuilt between each look — see `pixelSizeOverride` for why that
    // matters here specifically. Read per frame, which costs one `URLSearchParams` and means
    // the flag needs no plumbing through the engine.
    shaderEffect.setFloat4(
      'pixelParams',
      width,
      height,
      pixelSizeOverride() ?? PIXEL_SIZE,
      COLOUR_LEVELS,
    );
  };

  return effect;
}

/**
 * Whether to pixelate, allowing `?pixelate=0` to switch it off without a
 * rebuild.
 *
 * Worth the query-string read: the effect is the one part of the render path
 * that can black out the whole viewport on a driver it does not like, and being
 * able to rule it out in one reload is the difference between a two-minute
 * diagnosis and an afternoon.
 */
export function pixelateEnabled(): boolean {
  return PIXELATE && renderFeatures.pixelate;
}
